import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { 
  MapPinIcon, 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  GlobeAltIcon,
  PhoneIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { useAuth } from '../contexts/AuthContext'
import { useLocationsApi, Location } from '../services/locationsApi'
import LoadingSpinner from '../components/LoadingSpinner'
import CreateLocationModal from '../components/CreateLocationModal'
import EditLocationModal from '../components/EditLocationModal'

export default function LocationsPage() {
  const api = useLocationsApi()
  const { user } = useAuth0()
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState<Location | null>(null)
  const [deletingLocation, setDeletingLocation] = useState<string | null>(null)

  const { isAdmin } = useAuth()

  useEffect(() => {
    loadLocations()
  }, [])

  const loadLocations = async () => {
    try {
      setLoading(true)
      const data = await api.getLocations()
      setLocations(data.locations || [])
    } catch (error) {
      toast.error('Failed to load locations')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateLocation = async (locationData: any) => {
    try {
      const newLocation = await api.createLocation(locationData)
      setLocations(prev => [newLocation, ...prev])
      setShowCreateModal(false)
      toast.success('Location created successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to create location')
    }
  }

  const handleUpdateLocation = async (locationId: string, locationData: any) => {
    try {
      const updatedLocation = await api.updateLocation(locationId, locationData)
      setLocations(prev => 
        prev.map(loc => loc.locationId === locationId ? updatedLocation : loc)
      )
      setEditingLocation(null)
      toast.success('Location updated successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update location')
    }
  }

  const handleDeleteLocation = async (locationId: string) => {
    if (!window.confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      return
    }

    try {
      setDeletingLocation(locationId)
      await api.deleteLocation(locationId)
      setLocations(prev => prev.filter(loc => loc.locationId !== locationId))
      toast.success('Location deleted successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete location')
    } finally {
      setDeletingLocation(null)
    }
  }

  const formatAmenities = (amenities: string[]) => {
    if (!amenities || amenities.length === 0) return 'None'
    return amenities.join(', ')
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Locations</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Manage event venues and gathering places
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Location
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <BuildingOfficeIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Locations</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{locations.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Active</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {locations.filter(loc => loc.isActive).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <UserGroupIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Avg. Capacity</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {locations.length > 0 
                    ? Math.round(locations.filter(loc => loc.capacity).reduce((sum, loc) => sum + (loc.capacity || 0), 0) / locations.filter(loc => loc.capacity).length || 0)
                    : 0
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <motion.div
              key={location.locationId}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 transition-colors duration-300"
            >
              <div className="p-6">
                {/* Header with status and actions */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {location.name}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      location.isActive
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {location.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingLocation(location)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Edit location"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteLocation(location.locationId)}
                        disabled={deletingLocation === location.locationId}
                        className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                        title="Delete location"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Location Details */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <div>{location.address}</div>
                      {(location.city || location.state || location.zipCode) && (
                        <div>
                          {location.city}{location.city && location.state && ', '}{location.state} {location.zipCode}
                        </div>
                      )}
                    </div>
                  </div>

                  {location.phone && (
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">{location.phone}</span>
                    </div>
                  )}

                  {location.website && (
                    <div className="flex items-center space-x-2">
                      <GlobeAltIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <a 
                        href={location.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {location.website}
                      </a>
                    </div>
                  )}

                  {location.capacity && (
                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        Capacity: {location.capacity}
                      </span>
                    </div>
                  )}

                  {location.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-md p-3">
                      {location.description}
                    </div>
                  )}

                  {location.amenities && location.amenities.length > 0 && (
                    <div className="text-sm">
                      <span className="font-medium text-gray-900 dark:text-white">Amenities: </span>
                      <span className="text-gray-600 dark:text-gray-300">
                        {formatAmenities(location.amenities)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Created {new Date(location.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {locations.length === 0 && (
          <div className="text-center py-12">
            <MapPinIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No locations</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first location.
            </p>
            {isAdmin && (
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Location
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        <CreateLocationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateLocation={handleCreateLocation}
        />

        {editingLocation && (
          <EditLocationModal
            isOpen={true}
            location={editingLocation}
            onClose={() => setEditingLocation(null)}
            onUpdateLocation={handleUpdateLocation}
          />
        )}
      </motion.div>
    </div>
  )
}
